import React, { useState, useEffect } from 'react';
import { API_URL } from '../helpers';
import { useToast, useConfirm } from '../../shared/AppDialogs';

export function GoogleCalendarConfig({ config, onChange, agentId }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [status, setStatus] = useState({ connected: false, email: '', loading: true });
  const [connecting, setConnecting] = useState(false);

  const currentAgentId = agentId || window.location.pathname.split('/agent/')[1]?.split('/')[0]?.split('?')[0];

  useEffect(() => {
    loadStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true') {
      loadStatus();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [currentAgentId]);

  const loadStatus = async () => {
    if (!currentAgentId) {
      setStatus({ connected: false, email: '', loading: false });
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/agents/${currentAgentId}/google/status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus({ connected: data.connected, email: data.email || '', loading: false });
      } else {
        setStatus({ connected: false, email: '', loading: false });
      }
    } catch (err) {

      setStatus({ connected: false, email: '', loading: false });
    }
  };

  const connectGoogle = async () => {
    if (!currentAgentId) {
      toast.warning('Bitte speichern Sie den Agenten zuerst, bevor Sie Google Kalender verbinden.');
      return;
    }
    setConnecting(true);
    try {
      const response = await fetch(`${API_URL}/api/agents/${currentAgentId}/google/auth-url`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
      });
      const data = await response.json();
      if (data.success && data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error(data.detail || 'Verbindung fehlgeschlagen');
        setConnecting(false);
      }
    } catch (err) {
      toast.error(err.message);
      setConnecting(false);
    }
  };

  const disconnectGoogle = async () => {
    const ok = await confirm({
      title: 'Google Kalender trennen',
      message: 'Google Kalender Verbindung wirklich trennen?',
      confirmText: 'Trennen',
      type: 'danger',
    });
    if (!ok) return;
    try {
      const response = await fetch(`${API_URL}/api/agents/${currentAgentId}/google/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
      });
      if (response.ok) {
        setStatus({ connected: false, email: '', loading: false });
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <h4 className="font-medium text-gray-700">Google Kalender Verbindung</h4>
      {status.loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
          Lade Status...
        </div>
      ) : status.connected ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600">&#10003;</div>
            <div className="flex-1">
              <p className="font-medium text-emerald-800">Verbunden</p>
              <p className="text-sm text-emerald-600">{status.email}</p>
            </div>
            <button onClick={disconnectGoogle} className="text-red-600 hover:text-red-700 text-sm">Trennen</button>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Kalender ID (optional)</label>
            <input
              type="text"
              value={config?.calendar_id || 'primary'}
              onChange={(e) => onChange({ ...config, calendar_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="primary (Standard-Kalender)"
            />
            <p className="text-xs text-gray-400 mt-1">Leer lassen für Standard-Kalender</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Tage voraus</label>
            <input
              type="number"
              value={config?.days_ahead || 7}
              onChange={(e) => onChange({ ...config, days_ahead: parseInt(e.target.value) || 7 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              min="1"
              max="30"
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Verbinden Sie einen Google Kalender, damit dieser Agent auf Termine zugreifen kann.</p>
          {!currentAgentId && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              Bitte speichern Sie den Agenten zuerst, bevor Sie Google Kalender verbinden.
            </div>
          )}
          <button
            onClick={connectGoogle}
            disabled={connecting || !currentAgentId}
            className="w-full px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {connecting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-400 border-t-transparent"></div>
                Verbinden...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Mit Google verbinden
              </>
            )}
          </button>
        </div>
      )}
      <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
        <strong>So funktioniert's:</strong>
        <ul className="mt-1 ml-4 list-disc text-xs">
          <li>Jeder Agent kann mit einem eigenen Google Kalender verbunden werden</li>
          <li>Der Agent kann Termine aus dem Kalender abrufen</li>
          <li>Nur Lese-Zugriff - keine Termine werden erstellt oder geändert</li>
        </ul>
      </div>
    </div>
  );
}
